import React, { useEffect, useRef, useState } from 'react';

/**
 * Touch- and mouse-friendly reorder board.
 * Ghost clone follows the pointer; list reorders only on drop (smooth).
 */
export default function SortableBoard({ items, locked, cols, onReorder }) {
  const dragIdRef = useRef(null);
  const startIndexRef = useRef(-1);
  const hoverIndexRef = useRef(-1);
  const lockedRef = useRef(locked);
  lockedRef.current = locked;
  const onReorderRef = useRef(onReorder);
  onReorderRef.current = onReorder;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const listRef = useRef(null);
  const ghostRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });
  const [dragId, setDragId] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(-1);

  const endDrag = () => {
    const id = dragIdRef.current;
    const toIndex = hoverIndexRef.current;
    const fromIndex = startIndexRef.current;
    if (id != null && toIndex >= 0 && toIndex !== fromIndex) {
      onReorderRef.current(id, toIndex);
    }
    dragIdRef.current = null;
    startIndexRef.current = -1;
    hoverIndexRef.current = -1;
    setDragId(null);
    setHoverIndex(-1);
    if (ghostRef.current) {
      ghostRef.current.remove();
      ghostRef.current = null;
    }
  };

  useEffect(() => {
    if (!dragId) return undefined;

    const indexFromPoint = (clientX, clientY) => {
      const prev = ghostRef.current?.style.visibility;
      if (ghostRef.current) ghostRef.current.style.visibility = 'hidden';
      const el = document.elementFromPoint(clientX, clientY);
      if (ghostRef.current) ghostRef.current.style.visibility = prev || '';
      const slot = el?.closest?.('[data-sort-index]');
      if (!slot || !listRef.current?.contains(slot)) return -1;
      return Number(slot.getAttribute('data-sort-index'));
    };

    const onMove = (e) => {
      if (!dragIdRef.current) return;
      e.preventDefault();
      const g = ghostRef.current;
      if (g) {
        g.style.left = `${e.clientX - offsetRef.current.x}px`;
        g.style.top = `${e.clientY - offsetRef.current.y}px`;
      }
      const toIndex = indexFromPoint(e.clientX, e.clientY);
      if (toIndex < 0) return;
      const targetId = itemsRef.current[toIndex]?.id;
      if (targetId && lockedRef.current[targetId] && targetId !== dragIdRef.current) return;
      hoverIndexRef.current = toIndex;
      setHoverIndex(toIndex);
    };

    const onUp = () => endDrag();

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [dragId]);

  const onPointerDown = (e, id, index) => {
    if (locked[id] || e.button === 2) return;
    e.preventDefault();
    const itemEl = e.currentTarget;
    const rect = itemEl.getBoundingClientRect();
    offsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };

    const ghost = itemEl.cloneNode(true);
    ghost.classList.add('sop-sort-ghost');
    ghost.style.width = `${rect.width}px`;
    ghost.style.height = `${rect.height}px`;
    ghost.style.left = `${rect.left}px`;
    ghost.style.top = `${rect.top}px`;
    document.body.appendChild(ghost);
    ghostRef.current = ghost;

    dragIdRef.current = id;
    startIndexRef.current = index;
    hoverIndexRef.current = index;
    setDragId(id);
    setHoverIndex(index);
  };

  return (
    <div
      className={`sop-sort-board sop-sort-board-${cols}`}
      style={{ '--sop-sort-cols': cols }}
    >
      <ul ref={listRef} className="sop-sort-slots">
        {items.map((it, index) => {
          const isLocked = !!locked[it.id];
          const isDragging = dragId === it.id;
          const isHover = hoverIndex === index && dragId && dragId !== it.id;
          return (
            <li
              key={it.id}
              className={`sop-sort-slot ${isLocked ? 'locked' : ''} ${isDragging ? 'dragging' : ''} ${isHover ? 'drop-target' : ''}`}
              data-sort-id={it.id}
              data-sort-index={index}
            >
              <span className="sop-sort-num" aria-hidden="true">
                {index + 1}
              </span>
              <div
                className="sop-sort-item"
                role="button"
                tabIndex={isLocked ? -1 : 0}
                aria-grabbed={isDragging}
                aria-disabled={isLocked}
                onPointerDown={(e) => onPointerDown(e, it.id, index)}
              >
                <img src={it.src} alt="" draggable={false} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
